import React, { Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import CustomButton from "components/common/Button/CustomButton";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import { hasSpAdminLevelAccess } from "helpers/RolesPermission";
import {
  readOrgAiFromServiceProvider,
  SP_SETTINGS_AI_UPDATED_EVENT,
} from "helpers/spAiFeatureAccess";

const AI_SETTINGS_PATH = "/settings/ai-settings";

const ABOUT = [
  {
    title: "Incident concerns",
    body: "A safety issue, a medication issue, restrictive practice, or a shift note that describes an incident and has no form yet. A group shift can show one card per person. Create incident opens the form for that card. Acknowledge closes the card without filing one.",
  },
  {
    title: "Missed documentation",
    body: "A short or copied note, missing goals, unfinished end questions, open tasks, or a missing signature. This can sit on the same shift as an incident. It stays until you acknowledge it. Filing the incident does not close this row.",
  },
  {
    title: "Live shifts",
    body: "Shifts that are on the floor now, so you can see who is working before the timesheet is analysed.",
  },
];

const readNoticeFlags = () => {
  const org = readOrgAiFromServiceProvider();
  if (!org) {
    return { showAuto: false, showAnomaly: false };
  }

  const settings = GetItemFromLocalstorage("spSettings");
  const user = GetItemFromLocalstorage("user");
  const source =
    settings?.ai_org_permissions || settings?.ai_enabled !== undefined
      ? settings
      : user?.service_provider;
  const care = source?.ai_org_permissions?.care_watch;
  const autoKnown =
    care && typeof care === "object" && Object.prototype.hasOwnProperty.call(care, "auto_analyse");

  return {
    showAuto: Boolean(org.orgCareWatch) && autoKnown && !care.auto_analyse,
    showAnomaly: !org.orgCanAnalyse,
  };
};

const SettingsWord = () => (
  <Link to={AI_SETTINGS_PATH} className="care-watch-notice__link">
    AI settings
  </Link>
);

const NoticeBanner = ({ heading, children }) => (
  <div className="care-watch-notice" role="status">
    <p className="care-watch-notice__heading">{heading}</p>
    <p className="care-watch-notice__text">{children}</p>
  </div>
);

const CareWatchAboutDialog = ({ open, onClose }) => {
  const content = (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[120]" open={open} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/5 transition-all">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 bg-gradient px-5 py-4 text-white">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-inset ring-white/20">
                      <HiOutlineInformationCircle className="h-5 w-5" />
                    </span>
                    <Dialog.Title as="h2" className="text-base font-semibold leading-snug sm:text-lg">
                      What is Care Watch
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-1.5 text-white/80 transition hover:bg-white/10 hover:text-white"
                    aria-label="Close"
                  >
                    <HiOutlineX className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-5 py-5 sm:px-6">
                  <p className="text-sm leading-relaxed text-gray-600">
                    Care Watch shows timesheet analysis in one place. It lists
                    incidents, missing paperwork, and who is on shift now. The analysis
                    only suggests. It does not file an incident or change the timesheet.
                  </p>

                  <div className="mt-4 flex flex-col gap-2">
                    {ABOUT.map((item) => (
                      <div key={item.title} className="rounded-xl border border-gray-100 bg-slate-50/70 px-4 py-3">
                        <p className="text-sm font-semibold leading-snug text-gray-900">{item.title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-gray-500">{item.body}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:px-6">
                  <CustomButton
                    type="button"
                    variant="primary"
                    label="Got it"
                    clickHandler={onClose}
                    cssClass="!min-w-[6.5rem] justify-center"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  return createPortal(content, document.body);
};

const CareWatchNoticeBanner = () => {
  const [flags, setFlags] = useState(readNoticeFlags);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    const refresh = () => setFlags(readNoticeFlags());
    window.addEventListener(SP_SETTINGS_AI_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(SP_SETTINGS_AI_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  if (!hasSpAdminLevelAccess()) return null;
  if (!flags.showAuto && !flags.showAnomaly) return null;

  const openAbout = () => setAboutOpen(true);

  return (
    <>
      <div className="care-watch-notices">
        {flags.showAnomaly ? (
          <NoticeBanner heading="Care Watch is live – enable timesheet analysis first">
            Care Watch shows AI findings from shift notes once timesheets can be
            analysed. Turn on Timesheet anomaly check in <SettingsWord />, then
            use Auto-analyse for automatic reviews.{" "}
            <button type="button" className="care-watch-notice__link" onClick={openAbout}>
              What is Care Watch?
            </button>
          </NoticeBanner>
        ) : (
          <NoticeBanner heading="Care Watch is live – enable Auto-analyse for shift reviews">
            Care Watch is ready. Finished shifts are not analysed until you turn
            this on. Turn on Auto-analyse in <SettingsWord /> so each ended shift
            is reviewed automatically.{" "}
            <button type="button" className="care-watch-notice__link" onClick={openAbout}>
              What is Care Watch?
            </button>
          </NoticeBanner>
        )}
      </div>
      <CareWatchAboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
};

export default CareWatchNoticeBanner;
